import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Shield } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function SignupPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", external_user_id: "",
    email: "", password: "", company_id: "", role_id: "",
  });
  const [companies, setCompanies] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!form.company_id) { setRoles([]); return; }
    api.get(`/roles?company_id=${form.company_id}`).then((r) => setRoles(r.data));
  }, [form.company_id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const u = await register(form);
      toast.success(`Welcome, ${u.first_name}`);
      nav("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-950 text-slate-100">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r border-slate-800 grid-overlay">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 grid place-items-center rounded-md bg-blue-600/20 border border-blue-500/40">
            <Shield className="h-6 w-6 text-blue-400" />
          </div>
          <div>
            <div className="font-heading text-2xl font-semibold tracking-tight">Crew Ready</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">Risk · Rest · Readiness</div>
          </div>
        </div>
        <div>
          <h1 className="font-heading text-5xl font-bold tracking-tight leading-[1.05]">
            Join your<br />crew&apos;s<br /><span className="text-blue-400">readiness</span> console.
          </h1>
          <p className="mt-6 max-w-md text-slate-400 leading-relaxed">
            Pick your company and role, and start logging pre-duty fatigue signals in seconds.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono-num">
          No company yet? Ask an admin to add one.
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-2">Create account</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">Register</h2>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" testId="signup-first-name" value={form.first_name} onChange={set("first_name")} />
            <Field label="Last name" testId="signup-last-name" value={form.last_name} onChange={set("last_name")} />
          </div>
          <Field label="External user ID" testId="signup-external-id" value={form.external_user_id} onChange={set("external_user_id")} placeholder="e.g. CREW-1024" />
          <Field label="Email" type="email" testId="signup-email" value={form.email} onChange={set("email")} />
          <Field label="Password" type="password" testId="signup-password" value={form.password} onChange={set("password")} />
          <div className="grid grid-cols-2 gap-3">
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Company</label>
              <select data-testid="signup-company" value={form.company_id} onChange={set("company_id")} required
                className="w-full px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm">
                <option value="">Select…</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mt-4">
              <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">Role</label>
              <select data-testid="signup-role" value={form.role_id} onChange={set("role_id")} required disabled={!form.company_id}
                className="w-full px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm disabled:opacity-50">
                <option value="">{form.company_id ? "Select…" : "Pick company first"}</option>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          </div>
          {error && <div data-testid="signup-error" className="mt-4 text-sm rag-red px-3 py-2 rounded">{error}</div>}
          <button type="submit" disabled={loading} data-testid="signup-submit"
            className="mt-6 w-full py-2.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white font-medium transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
          </button>
          <div className="mt-5 text-sm text-slate-400 text-center">
            Already have an account? <Link to="/login" className="text-blue-400 hover:text-blue-300" data-testid="link-login">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, testId, type = "text", ...rest }) {
  return (
    <div className="mt-4">
      <label className="block text-xs uppercase tracking-widest text-slate-400 font-mono-num mb-1.5">{label}</label>
      {type === "password" ? (
        <PasswordInput testId={testId} required {...rest}
          className="w-full px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm" />
      ) : (
        <input data-testid={testId} type={type} required {...rest}
          className="w-full px-3 py-2.5 rounded-md bg-slate-900 border border-slate-800 focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40 outline-none text-sm" />
      )}
    </div>
  );
}
